
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export interface WebhookConfiguration {
  id: string;
  user_id: string;
  webhook_url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Corrigindo a interface para corresponder à tabela webhook_logs
export interface WebhookLog {
  id: string;
  user_id: string | null;
  customer_id: string | null;
  event_type: string;
  event_id: string | null;
  data: any;
  status: string;
  error: string | null;
  created_at: string;
}

export const useWebhookConfiguration = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);

  // Buscar configuração de webhook
  const { data: webhookConfig, isLoading: isLoadingConfig } = useQuery({
    queryKey: ['webhook-configuration', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('webhook_configurations')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data as WebhookConfiguration | null;
    },
    enabled: !!user?.id,
  });

  // Buscar logs de webhook
  const { data: webhookLogs, isLoading: isLoadingLogs } = useQuery({
    queryKey: ['webhook-logs', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('webhook_logs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as WebhookLog[];
    },
    enabled: !!user?.id,
  });

  // Salvar/atualizar configuração de webhook
  const saveWebhookConfig = useMutation({
    mutationFn: async (data: { webhook_url: string; is_active: boolean }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const configData = {
        user_id: user.id,
        webhook_url: data.webhook_url,
        is_active: data.is_active,
        updated_at: new Date().toISOString()
      };

      if (webhookConfig?.id) {
        // Atualizar existente
        const { data: result, error } = await supabase
          .from('webhook_configurations')
          .update(configData)
          .eq('id', webhookConfig.id)
          .select()
          .single();

        if (error) throw error;
        return result;
      } else {
        // Criar novo
        const { data: result, error } = await supabase
          .from('webhook_configurations')
          .insert(configData)
          .select()
          .single();

        if (error) throw error;
        return result;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-configuration'] });
      toast.success('Configuração de webhook salva com sucesso');
    },
    onError: (error) => {
      console.error('Error saving webhook configuration:', error);
      toast.error('Erro ao salvar configuração de webhook');
    },
  });

  // Testar webhook
  const testWebhook = async (webhookUrl: string) => {
    setIsTestingWebhook(true);
    
    try {
      const testPayload = {
        event: 'campaign.completed',
        timestamp: new Date().toISOString(),
        data: {
          campaign_id: 'test-campaign-id',
          campaign_name: 'Teste de Webhook',
          new_status: 'completed',
          user_id: user?.id,
          coupon_codes: ['TEST_COUPON_1', 'TEST_COUPON_2'],
          completed_at: new Date().toISOString()
        }
      };

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testPayload),
      });

      if (response.ok) {
        toast.success('Webhook testado com sucesso!');
        return true;
      } else {
        toast.error(`Erro no teste: HTTP ${response.status}`);
        return false;
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      toast.error('Erro ao testar webhook. Verifique a URL.');
      return false;
    } finally {
      setIsTestingWebhook(false);
    }
  };

  return {
    webhookConfig,
    webhookLogs,
    isLoadingConfig,
    isLoadingLogs,
    isTestingWebhook,
    saveWebhookConfig,
    testWebhook,
  };
};
